import {
    HttpClient,
    HttpHeaders,
    HttpParams,
    HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BaseSearchOptions } from '../models/core/table/BaseSearchOptions';
import { CreateB2BClient } from '../models/domain/B2BClient/CreateB2BClient';
import { GetB2BClient } from '../models/domain/B2BClient/GetB2BClient';
import { UpdateB2BClient } from '../models/domain/B2BClient/UpdateB2BClient';
import { CreateB2CClient } from '../models/domain/B2CClient/CreateB2CClient';
import { GetB2CClient } from '../models/domain/B2CClient/GetB2CClient';
import { UpdateB2CClient } from '../models/domain/B2CClient/UpdateB2CClient';
import { PngIdentityUser } from '../models/domain/pngIdentityUser';
import { RoleEnum } from '../models/enums/RoleEnum';
import { AuthenticationResult } from '../models/general/AuthenticationResult';
import { ChangePassword } from '../models/general/ChangePassword';
import { PagedResponse } from '../models/general/PagedResponse';
import { ResetPasswordRequest } from '../models/general/ResetPasswordRequest';
import { UserConfirmation } from '../models/general/UserConfirmation';
import { JWTTokenService } from './jwttoken.service';
import { LoadingService } from './loading.service';
import { LocalStorageService, StorageKeys } from './local-storage.service';

@Injectable({
    providedIn: 'root',
})
export class AuthenticationService {
    private currentAuthSubject: BehaviorSubject<AuthenticationResult | null>;
    public currentAuth$: Observable<AuthenticationResult | null>;
    private userBaseUrl = `${environment.apiUrl}/v1/identity`;
    private refreshTokenTimeout?: any;
    private isAuthenticated = false;

    constructor(
        private http: HttpClient,
        private localStorage: LocalStorageService,
        private router: Router,
        private loadingService: LoadingService,
        private jwtTokenService: JWTTokenService
    ) {
        if (this.localStorage.get(StorageKeys.CurrentAuth)) {
            const token = JSON.parse(
                this.localStorage.get(StorageKeys.CurrentAuth) ?? ''
            );
            this.currentAuthSubject =
                new BehaviorSubject<AuthenticationResult | null>(token);
            this.currentAuth$ = this.currentAuthSubject.asObservable();
        } else {
            this.currentAuthSubject =
                new BehaviorSubject<AuthenticationResult | null>(null);
            this.currentAuth$ = this.currentAuthSubject.asObservable();
        }

        //this.startRefreshTokenTimer();
    }

    public get userValue() {
        return this.currentAuthSubject.value;
    }

    public login(
        email: string,
        password: string,
        resent: boolean
    ): Observable<AuthenticationResult> {
        const requestData = {
            email: email,
            password: password,
        };

        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json; charset=utf-8',
            }),
            withCredentials: true, // Make sure this is set to true
        };

        return this.http
            .post<any>(`${this.userBaseUrl}/login`, requestData, httpOptions)
            .pipe(
                map((result: AuthenticationResult) => {
                    if (result.isSuccess) {
                        this.setUserInformationAndRoles(result);

                        this.localStorage.set(
                            StorageKeys.Token,
                            result.accessToken
                        );
                        this.localStorage.set(
                            StorageKeys.CurrentAuth,
                            JSON.stringify(result)
                        );

                        this.currentAuthSubject.next(result);

                        //this.startRefreshTokenTimer();
                        return result;
                    } else {
                        this.currentAuthSubject.next(result);
                        return result;
                    }
                })
            );
    }

    public logout(): void {
        this.localStorage.remove(StorageKeys.CurrentAuth);
        this.localStorage.remove(StorageKeys.Token);
        this.currentAuthSubject.next(null);
        this.router.navigate(['/login']);
    }

    async checkAuthenticationState(): Promise<void> {
        return new Promise(async (resolve) => {
            const token = this.isUserAuthenticated();
            this.isAuthenticated = !!token;
            await new Promise((resolve) => setTimeout(resolve, 100)); // Simulate delay
        });
    }

    isUserAuthenticated1(): boolean {
        return this.isAuthenticated;
    }

    public isUserAuthenticated(): boolean {
        if (
            this.currentAuthSubject &&
            this.currentAuthSubject.value?.user !== null &&
            this.currentAuthSubject.value?.user != undefined
        ) {
            return true;
        }
        return false;
    }

    public isUserRoleAuthorized(roles: string[]): boolean {
        if (roles) {
            const availableRoles = this.getUserRoles();
            if (
                availableRoles.some(
                    (x) =>
                        x.toLowerCase() == RoleEnum.ProductMaster.toLowerCase()
                ) ||
                roles.some((x) =>
                    availableRoles.some(
                        (y) => y.toLowerCase() === x.toLowerCase()
                    )
                )
            ) {
                return true;
            }
        }
        return false;
    }

    public getUserRoles(): string[] {
        return this.getCurrentUser()?.roles || [];
    }

    public hasRole(role: string, bypassSuperAdmin: boolean = false): boolean {
        const roles = this.getUserRoles();
        if (roles.includes(RoleEnum.ProductMaster) && !bypassSuperAdmin) {
            return true; // Super admin bypass
        }
        return roles.includes(role);
    }

    public hasRoles(requiredRoles: string[], bypassSuperAdmin: boolean = false): boolean {
        const roles = this.getUserRoles();
        if (roles.includes(RoleEnum.ProductMaster) && !bypassSuperAdmin) {
            return true; // Super admin bypass
        }
        return requiredRoles.some(role => roles.includes(role));
    }


    public getCurrentUser(): PngIdentityUser | null {
        if (
            this.currentAuthSubject &&
            this.currentAuthSubject.value?.user !== null &&
            this.currentAuthSubject.value?.user != undefined
        ) {
            return this.currentAuthSubject?.value?.user;
        }
        return null;
    }

    private setUserInformationAndRoles(
        result: AuthenticationResult
    ): AuthenticationResult {
        // Decode the JWT token to get the payload (claims)
        const jwtData = this.jwtTokenService.getTokenData(result.accessToken);
        if (jwtData) {
            result.user = result.user ?? new PngIdentityUser();
            result.user.roles = result.user.roles ?? [];

            result.user.email = jwtData['userEmail'];
            result.user.fullName = jwtData['fullName'];
            result.user.id = parseInt(jwtData['id']);

            result.user.clientId = parseInt(jwtData['clientId']);
            result.user.parentTenantId = parseInt(jwtData['parentTenantId']);
            result.user.tenantId = parseInt(jwtData['tenantId']);

            // Parse paymentTerms from JWT claim (stored as JSON array string)
            const rawTerms = jwtData['paymentTerms'];
            if (rawTerms) {
                try {
                    result.user.paymentTerms = JSON.parse(rawTerms);
                } catch {
                    result.user.paymentTerms = [];
                }
            }

            const rolesData =
                jwtData[
                'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
                ];

            if (rolesData !== undefined && Array.isArray(rolesData)) {
                // If rolesData is an array, iterate through and push the roles to the user
                rolesData.forEach((role) => {
                    result.user.roles.push(role);
                });
            } else if (rolesData !== undefined) {
                // If rolesData is a single value (not an array), just push the role
                result.user.roles.push(rolesData);
            }
        }

        return result;
    }

    public OTPVerify(
        otp: string,
        token: string
    ): Observable<AuthenticationResult> {
        return this.http
            .post<any>(`${this.userBaseUrl}/otpverificationforlogin`, {
                otp,
                token,
            })
            .pipe(
                map((result: AuthenticationResult) => {
                    if (result.isSuccess) {
                        this.currentAuthSubject.next(result);
                        this.localStorage.set(
                            StorageKeys.Token,
                            result.accessToken
                        );
                        this.localStorage.set(
                            StorageKeys.CurrentAuth,
                            JSON.stringify(result)
                        );
                        return result;
                    } else {
                        this.currentAuthSubject.next(result);
                        return result;
                    }
                })
            );
    }

    public getEmail(token: string): Observable<unknown> {
        const apiUrl = `${this.userBaseUrl}/email?token=${token}`;

        return this.http.get<unknown>(apiUrl);
    }

    public confirmAccount(model: UserConfirmation): Observable<unknown> {
        const apiUrl = `${this.userBaseUrl}/confirmAccount`;

        return this.http.post(apiUrl, model);
    }

    public forgotPassword(email: string): Observable<HttpResponse<any>> {
        this.loadingService.showLoading();

        return this.http.post<any>(`${environment.apiUrl}/v1/identity/forgotPassword`, { email }, { observe: "response" })
            .pipe(
                tap((response: HttpResponse<any>) => {
                    this.loadingService.hideLoading();
                }),
                catchError(error => {
                    this.loadingService.hideLoading();
                    return throwError(() => error);
                }),
                finalize(() => {
                    this.loadingService.hideLoading();
                })
            );
    }

    //Not in use right now
    // public sendForgotPasswordEmail(model: UserConfirmation): Observable<any> {
    //     const apiUrl = `${this.userBaseUrl}/sendForgotPasswordEmail`;
    //     return this.http.post(apiUrl, model);
    // }

    // public changePassword(model: ChangePassword): Observable<any> {
    //     const apiUrl = `${this.userBaseUrl}/changePassword`;
    //
    //     return this.http.post(apiUrl, model);
    // }

    public refreshToken() {
        return this.http
            .post<AuthenticationResult>(
                `${this.userBaseUrl}/refresh-token`,
                {},
                { withCredentials: true }
            )
            .pipe(
                map((result: AuthenticationResult) => {
                    this.currentAuthSubject.next(result);
                    this.localStorage.set(
                        StorageKeys.Token,
                        result.accessToken
                    );
                    this.localStorage.set(
                        StorageKeys.CurrentAuth,
                        JSON.stringify(result)
                    );
                    this.startRefreshTokenTimer();
                    return result;
                })
            );
    }

    public revokeToken(): void {
        this.http
            .post<unknown>(
                `${this.userBaseUrl}/revoke-token`,
                {},
                { withCredentials: true }
            )
            .subscribe();
    }

    private startRefreshTokenTimer() {
        const accessToken = this.localStorage.get(StorageKeys.Token);
        if (accessToken == null || accessToken == undefined) {
            this.stopRefreshTokenTimer();
        } else {
            const jwtToken = JSON.parse(atob(accessToken.split('.')[1]));
            // set a timeout to refresh the token a minute before it expires
            const expires = new Date(jwtToken.exp * 1000);
            const oneMinuteBeforeExpire =
                expires.getTime() - Date.now() - 60 * 1000;

            this.refreshTokenTimeout = setTimeout(
                () => this.refreshToken().subscribe(),
                oneMinuteBeforeExpire
            );
        }
    }

    private stopRefreshTokenTimer() {
        clearTimeout(this.refreshTokenTimeout);
    }

    public createB2BClient(
        obj: CreateB2BClient
    ): Observable<HttpResponse<CreateB2BClient>> {
        return this.http
            .post<CreateB2BClient>(`${this.userBaseUrl}/createB2BTenant`, obj, {
                observe: 'response',
            })
            .pipe(
                catchError((error) => {
                    return throwError(() => error); // Make sure to throw the error properly
                }),
                finalize(() => { })
            );
    }

    public createB2CClient(
        obj: CreateB2CClient
    ): Observable<HttpResponse<CreateB2CClient>> {
        return this.http
            .post<CreateB2CClient>(`${this.userBaseUrl}/createB2CTenant`, obj, {
                observe: 'response',
            })
            .pipe(
                catchError((error) => {
                    return throwError(() => error); // Make sure to throw the error properly
                }),
                finalize(() => { })
            );
    }

    public updateB2BClient(
        obj: UpdateB2BClient
    ): Observable<HttpResponse<UpdateB2BClient>> {
        return this.http
            .put<UpdateB2BClient>(`${this.userBaseUrl}/updateB2BTenant`, obj, {
                observe: 'response',
            })
            .pipe(
                catchError((error) => {
                    return throwError(() => error); // Make sure to throw the error properly
                }),
                finalize(() => { })
            );
    }

    public updateB2CClient(
        obj: UpdateB2CClient
    ): Observable<HttpResponse<UpdateB2CClient>> {
        return this.http
            .put<UpdateB2CClient>(`${this.userBaseUrl}/updateB2CTenant`, obj, {
                observe: 'response',
            })
            .pipe(
                catchError((error) => {
                    return throwError(() => error); // Make sure to throw the error properly
                }),
                finalize(() => { })
            );
    }

    public getB2bClient(
        parameters: BaseSearchOptions
    ): Observable<HttpResponse<PagedResponse<GetB2BClient>>> {
        const params = new HttpParams()
            .set('filter', parameters.filter)
            .set('orderBy', parameters.orderBy)
            .set('pageSize', parameters.pageSize)
            .set('pageIndex', parameters.pageIndex)
            .set('orderByDirection', parameters.orderByDirection)
            .set('expand', parameters.expand);

        return this.http
            .get<PagedResponse<GetB2BClient>>(`${this.userBaseUrl}/GetB2BClientAsync`, {
                params,
                observe: 'response',
            })
            .pipe(
                catchError((error) => {
                    return throwError(() => error); // Make sure to throw the error properly
                }),
                finalize(() => { })
            );
    }

    public getB2cClient(
        parameters: BaseSearchOptions
    ): Observable<HttpResponse<PagedResponse<GetB2CClient>>> {
        const params = new HttpParams()
            .set('filter', parameters.filter)
            .set('orderBy', parameters.orderBy)
            .set('pageSize', parameters.pageSize)
            .set('pageIndex', parameters.pageIndex)
            .set('orderByDirection', parameters.orderByDirection)
            .set('expand', parameters.expand);

        return this.http
            .get<PagedResponse<GetB2CClient>>(`${this.userBaseUrl}/GetB2CClientAsync`, {
                params,
                observe: 'response',
            })
            .pipe(
                catchError((error) => {
                    return throwError(() => error); // Make sure to throw the error properly
                }),
                finalize(() => { })
            );
    }


    public deleteB2bClient(id: any): Observable<HttpResponse<any>> {
        return this.http
            .delete(`${this.userBaseUrl}/${id}`, { observe: 'response' })
            .pipe(
                tap((response: HttpResponse<any>) => {
                    if (response.ok) {
                    }
                }),
                catchError((error) => {
                    // Propagate the error properly to the ErrorInterceptor
                    return throwError(() => error); // Make sure to throw the error properly
                }),
                finalize(() => { })
            );
    }

    public deleteB2cClient(id: any): Observable<HttpResponse<any>> {
        return this.http
            .delete(`${this.userBaseUrl}/${id}`, { observe: 'response' })
            .pipe(
                tap((response: HttpResponse<any>) => {
                    if (response.ok) {
                    }
                }),
                catchError((error) => {
                    // Propagate the error properly to the ErrorInterceptor
                    return throwError(() => error); // Make sure to throw the error properly
                }),
                finalize(() => { })
            );
    }

    public updateProfile(obj: PngIdentityUser): Observable<HttpResponse<PngIdentityUser>> {
        this.loadingService.showLoading();

        return this.http.put<PngIdentityUser>(`${environment.apiUrl}/v1/identity/update-profile`, obj, { observe: "response" })
            .pipe(tap((response: HttpResponse<PngIdentityUser>) => {
                this.loadingService.hideLoading();
                return response
            }),
                catchError(error => {
                    // Propagate the error properly to the ErrorInterceptor
                    this.loadingService.hideLoading();
                    return throwError(() => error); // Make sure to throw the error properly
                }),
                finalize(() => {
                    this.loadingService.hideLoading();
                })
            );
    }

    public resetPassword(obj: ResetPasswordRequest): Observable<HttpResponse<any>> {
        this.loadingService.showLoading();

        return this.http.post<any>(`${environment.apiUrl}/v1/identity/resetPassword`, obj, { observe: "response" })
            .pipe(tap((response: HttpResponse<any>) => {
                this.loadingService.hideLoading();
                return response
            }),
                catchError(error => {
                    // Propagate the error properly to the ErrorInterceptor
                    this.loadingService.hideLoading();
                    return throwError(() => error); // Make sure to throw the error properly
                }),
                finalize(() => {
                    this.loadingService.hideLoading();
                })
            );
    }


    public changePassword(obj: ChangePassword): Observable<HttpResponse<any>> {
        this.loadingService.showLoading();

        return this.http.put<any>(`${environment.apiUrl}/v1/identity/change-password`, obj, { observe: "response" })
            .pipe(tap((response: HttpResponse<any>) => {
                this.loadingService.hideLoading();
                return response
            }),
                catchError(error => {
                    // Propagate the error properly to the ErrorInterceptor
                    this.loadingService.hideLoading();
                    return throwError(() => error); // Make sure to throw the error properly
                }),
                finalize(() => {
                    this.loadingService.hideLoading();
                })
            );
    }
}
